import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import api, { getApiUrl } from '../utils/api';

const API_URL = getApiUrl();

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState({
    logo_url: '',
    logo_visible: true,
    site_name: 'BMS',
    site_name_full: 'BrightMind Software'
  });
  const { language, t, switchLanguage } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings');
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: '/hizmetler', label: t.nav.services },
    { to: '/projeler', label: t.nav.projects },
    { to: '/hakkimizda', label: t.nav.about },
    { to: '/iletisim', label: t.nav.contact },
  ];

  const logoUrl = settings.logo_url ? 
    (settings.logo_url.startsWith('http') ? settings.logo_url : `${API_URL}${settings.logo_url}`) : 
    null;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'py-3 bg-white/95 backdrop-blur-md shadow-corporate border-b border-gray-100' 
          : 'py-4 bg-white'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          {settings.logo_visible && (
            <>
              {logoUrl ? (
                <img 
                  src={logoUrl} 
                  alt={settings.site_name}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-bms-primary to-bms-primary-light flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
              )}
            </>
          )}
          <span className="text-xl font-bold text-bms-text tracking-wide">{settings.site_name}</span>
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link 
              key={link.to}
              to={link.to} 
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.to || location.pathname.startsWith(link.to + '/')
                  ? 'text-bms-primary' 
                  : 'text-bms-text-light hover:text-bms-primary'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
        
        {/* Right Side: Language + CTA */}
        <div className="flex items-center gap-4">
          {/* Language Switcher */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="language-switcher">
            <button
              onClick={() => switchLanguage('tr')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                language === 'tr' 
                  ? 'bg-bms-primary text-white' 
                  : 'text-bms-text-light hover:text-bms-primary'
              }`}
            >
              TR
            </button>
            <button
              onClick={() => switchLanguage('en')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                language === 'en' 
                  ? 'bg-bms-primary text-white' 
                  : 'text-bms-text-light hover:text-bms-primary'
              }`}
            >
              EN
            </button>
          </div>
          
          {/* CTA Button */}
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Link
              to="/iletisim"
              data-testid="nav-cta"
              className="hidden sm:block px-5 py-2.5 bg-bms-primary hover:bg-bms-primary-dark text-white rounded-lg text-sm font-semibold 
                       transition-all duration-300 shadow-blue hover:shadow-blue-lg"
            >
              {t.nav.getInTouch}
            </Link>
          </motion.div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-bms-text hover:text-bms-primary transition-colors"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-corporate-lg"
        >
          <div className="px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg transition-colors ${
                  location.pathname === link.to 
                    ? 'bg-blue-50 text-bms-primary font-medium' 
                    : 'text-bms-text-light hover:bg-gray-50 hover:text-bms-primary'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/iletisim"
              onClick={() => setMobileMenuOpen(false)}
              className="block mt-4 py-3 px-6 bg-bms-primary text-white rounded-lg text-center font-semibold hover:bg-bms-primary-dark transition-colors"
            >
              {t.nav.getInTouch}
            </Link>
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
};

export default Navbar;
